import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import { EmailTemplateModal } from '@components/Modal/PickupEmailTemplates/EmailTemplateModal';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useToast } from '@contexts/Toast';
import {
  faChevronDown,
  faChevronUp,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import {
  errorMessage,
  onboardingStepId,
  PickUpSettingsList,
  productSubsteps,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  NotificationTemplates,
  StorageCostFormProps,
  SelectedTemplateProps,
} from '@helpers/Models';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';

export const Pickup: React.FC = () => {
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const { updateOnboardingStep } = useLocalStorage();
  const [applicable, setApplicable] = useState<boolean>(false);
  const [openAccordions, setOpenAccordions] = useState<{
    [key: string]: boolean;
  }>({
    pickupReady: true,
    lateFee: true,
  });

  const [notificationTemplates, setNotificationTemplates] =
    useState<NotificationTemplates>({});
  const [formData, setFormData] = useState<StorageCostFormProps>({
    notificationDays: 0,
    freeStorageDays: 0,
    isApplicable: 0,
    storageCostPerDay: 0,
    resendNotificationCount: 0,
    orderEstDays: 0,
  });
  const [initialFormData, setInitialFormData] =
    useState<StorageCostFormProps>();
  const [selectedreadyForPickupSms, setSelectedreadyForPickupSms] = useState<
    number | null
  >(null);
  const [selectedreadyForPickupEmail, setSelectedreadyForPickupEmail] =
    useState<number | null>(null);
  const [selectedlateFeeSms, setSelectedlateFeeSms] = useState<number | null>(
    null,
  );
  const [selectedlateFeeEmail, setSelectedlateFeeEmail] = useState<
    number | null
  >(null);

  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<
    string | null
  >(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<
    SelectedTemplateProps[]
  >([]);

  // State for unsaved changes – updated by useEffect
  const [hasChanges, setHasChanges] = useState(false);
  const [hasCreatedRecord, setHasCreatedRecord] = useState(false);

  // Hook to block navigation when there are unsaved changes
  useUnsavedChangesPrompt(hasChanges, undefined, 'Leave Without Saving');

  useEffect(() => {
    fetchStorageCost();
  }, []);

  // Detect changes whenever any relevant state updates
  useEffect(() => {
    if (!initialFormData) return;

    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);

    // Compute current template IDs from selected state variables
    const currentIds: number[] = [];
    if (selectedreadyForPickupSms) currentIds.push(selectedreadyForPickupSms);
    if (selectedreadyForPickupEmail) currentIds.push(selectedreadyForPickupEmail);
    if (applicable) {
      if (selectedlateFeeSms) currentIds.push(selectedlateFeeSms);
      if (selectedlateFeeEmail) currentIds.push(selectedlateFeeEmail);
    }

    const initialIds = selectedTemplates
      .map((t) => t.notificationTemplateId)
      .sort();
    const currentIdsSorted = [...currentIds].sort();
    const templatesChanged =
      JSON.stringify(currentIdsSorted) !== JSON.stringify(initialIds);

    setHasChanges(formChanged || templatesChanged);
  }, [
    formData,
    initialFormData,
    applicable,
    selectedreadyForPickupSms,
    selectedreadyForPickupEmail,
    selectedlateFeeSms,
    selectedlateFeeEmail,
    selectedTemplates,
  ]);

  // When applicable becomes true, restore late fee templates from saved state if available
  useEffect(() => {
    if (applicable) {
      // If there are saved templates with at least 4 entries, assume last two are late fee templates
      if (selectedTemplates.length >= 4) {
        setSelectedlateFeeSms(selectedTemplates[2]?.notificationTemplateId || null);
        setSelectedlateFeeEmail(selectedTemplates[3]?.notificationTemplateId || null);
      } else {
        // No saved late fee templates, keep them null
        setSelectedlateFeeSms(null);
        setSelectedlateFeeEmail(null);
      }
    } else {
      // Clear late fee templates when applicable is off
      setSelectedlateFeeSms(null);
      setSelectedlateFeeEmail(null);
    }
  }, [applicable, selectedTemplates]);

  const toggleAccordion = (key: string): void => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const fetchStorageCost = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.STORAGE_COST_LIST);

      const storageCostData = response.data.data.storageCost;
      const orderEstDaysData = response.data.data.orderEstDays?.orderEstDays ?? 0;
      const templates = storageCostData?.StorageCostNotificationTemplates || [];

      const defaultForm: StorageCostFormProps = {
        notificationDays: 0,
        freeStorageDays: 0,
        isApplicable: 0,
        storageCostPerDay: 0,
        resendNotificationCount: 0,
        orderEstDays: 0,
      };

      if (storageCostData) {
        setHasCreatedRecord(true);
        const isApplicable = storageCostData.isApplicable ?? false;
        setApplicable(isApplicable);
        const initial = {
          notificationDays: storageCostData.notificationDays ?? 0,
          freeStorageDays: storageCostData.freeStorageDays ?? 0,
          isApplicable: isApplicable ? 1 : 0,
          storageCostPerDay: storageCostData.storageCostPerDay ?? 0,
          resendNotificationCount: storageCostData.resendNotificationCount ?? 0,
          orderEstDays: orderEstDaysData,
        };
        setInitialFormData(initial);
        setFormData(initial);
        setSelectedTemplates(templates);

        // Map templates to individual state variables.
        // Order: [SMS_OrderReady, Email_OrderReady, SMS_LateFee, Email_LateFee]
        if (templates.length > 0) {
          setSelectedreadyForPickupSms(templates[0]?.notificationTemplateId || null);
          setSelectedreadyForPickupEmail(templates[1]?.notificationTemplateId || null);
          if (isApplicable && templates.length >= 4) {
            setSelectedlateFeeSms(templates[2]?.notificationTemplateId || null);
            setSelectedlateFeeEmail(templates[3]?.notificationTemplateId || null);
          } else {
            setSelectedlateFeeSms(null);
            setSelectedlateFeeEmail(null);
          }
        } else {
          // No saved templates – reset selections
          setSelectedreadyForPickupSms(null);
          setSelectedreadyForPickupEmail(null);
          setSelectedlateFeeSms(null);
          setSelectedlateFeeEmail(null);
        }
      } else {
        // No data – reset everything
        setHasCreatedRecord(false);
        setApplicable(false);
        setInitialFormData(defaultForm);
        setFormData(defaultForm);
        setSelectedTemplates([]);
        setSelectedreadyForPickupSms(null);
        setSelectedreadyForPickupEmail(null);
        setSelectedlateFeeSms(null);
        setSelectedlateFeeEmail(null);
      }
      setNotificationTemplates(response.data.data.notificationTemplates);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const isTemplateChecked = (
    selectedId: number | null,
    templateId: number,
  ): boolean => {
    if (selectedId !== null) {
      return selectedId === templateId;
    }
    return selectedTemplates.some(
      (sel) => sel.notificationTemplateId === templateId,
    );
  };

  const getNumericValue = (value: string | number): number => {
    if (typeof value === 'string') {
      return value === '' ? 0 : parseFloat(value) || 0;
    }
    return value;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    const numericValue: number | string = value === '' ? '' : parseFloat(value) || 0;

    setFormData((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  const handleViewEmailTemplate = (content: string): void => {
    setSelectedEmailTemplate(content);
    setShowModal(true);
  };

  /**
   * Validates the storage cost form data and returns sanitised payload.
   */
  const validateFormData = (): {
    isValid: boolean;
    error?: string;
    finalFormData?: StorageCostFormProps;
    finalTemplateIds?: number[];
  } => {
    const numericFormData = {
      orderEstDays: getNumericValue(formData.orderEstDays),
      freeStorageDays: getNumericValue(formData.freeStorageDays),
      resendNotificationCount: getNumericValue(
        formData.resendNotificationCount,
      ),
      notificationDays: getNumericValue(formData.notificationDays),
      storageCostPerDay: getNumericValue(formData.storageCostPerDay),
      isApplicable: formData.isApplicable,
    };

    const finalFormData = numericFormData;
    const finalTemplateIds: number[] = [];

    // Build template IDs from state variables
    if (selectedreadyForPickupSms && selectedreadyForPickupSms > 0)
      finalTemplateIds.push(selectedreadyForPickupSms);
    if (selectedreadyForPickupEmail && selectedreadyForPickupEmail > 0)
      finalTemplateIds.push(selectedreadyForPickupEmail);
    if (applicable) {
      if (selectedlateFeeSms && selectedlateFeeSms > 0)
        finalTemplateIds.push(selectedlateFeeSms);
      if (selectedlateFeeEmail && selectedlateFeeEmail > 0)
        finalTemplateIds.push(selectedlateFeeEmail);
    }

    const errorMessages: string[] = [];

    // All numeric fields must be >= 0
    if (numericFormData.orderEstDays < 0) {
      errorMessages.push('Estimated days to complete order cannot be negative.');
    }
    if (numericFormData.freeStorageDays < 0) {
      errorMessages.push('Free storage days cannot be negative.');
    }
    if (numericFormData.resendNotificationCount < 0) {
      errorMessages.push('Resend notification count cannot be negative.');
    }

    if (applicable) {
      // Notification days must be > 0
      if (numericFormData.notificationDays <= 0) {
        errorMessages.push('Notification days must be greater than 0.');
      }
      // Storage cost per day must be > 0
      if (numericFormData.storageCostPerDay <= 0) {
        errorMessages.push('Storage cost per day must be greater than 0.');
      }
      if (numericFormData.freeStorageDays < numericFormData.notificationDays) {
        errorMessages.push(
          'Free storage days must be greater than or equal to notification days.'
        );
      }
    }

    // Template selection validation
    if (applicable) {
      if (finalTemplateIds.length < 4) {
        const missing: string[] = [];
        if (!selectedreadyForPickupSms) missing.push('SMS for Order Ready');
        if (!selectedreadyForPickupEmail) missing.push('Email for Order Ready');
        if (!selectedlateFeeSms) missing.push('SMS for Late Fee');
        if (!selectedlateFeeEmail) missing.push('Email for Late Fee');
        if (missing.length > 0) {
          errorMessages.push(
            `Please select the following templates: ${missing.join(', ')}.`
          );
        }
      }
    } else {
      if (finalTemplateIds.length < 2) {
        const missing: string[] = [];
        if (!selectedreadyForPickupSms) missing.push('SMS for Order Ready');
        if (!selectedreadyForPickupEmail) missing.push('Email for Order Ready');
        errorMessages.push(
          `Please select the following templates: ${missing.join(', ')}.`
        );
      }
    }

    if (errorMessages.length > 0) {
      return { isValid: false, error: errorMessages.join(' ') };
    }

    if (!applicable) {
      finalFormData.notificationDays = 0;
      finalFormData.storageCostPerDay = 0;
    }

    return { isValid: true, finalFormData, finalTemplateIds };
  };

  /**
   * Saves the current form data and (optionally) navigates to the next page.
   */
  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
  ) => Promise<void> = async (event, nextPage) => {
    event.preventDefault();

    const { isValid, error, finalFormData, finalTemplateIds } =
      validateFormData();

    if (!isValid) {
      if (error) {
        showToastMessage(error, 'error');
      }
      return;
    }

    if (!finalFormData || !finalTemplateIds) {
      showToastMessage('Unable to save. Please review the form and try again.', 'error');
      return;
    }

    const safeFinalFormData = finalFormData;
    const safeFinalTemplateIds = finalTemplateIds;

    const payload = {
      ...safeFinalFormData,
      notificationTemplateIds: safeFinalTemplateIds,
    };

    try {
      await Axios.post(
        ApiEndpoints.STORAGE_COST_UPSET,
        payload,
      );

      updateOnboardingStep(onboardingStepId.STORAGE_COST);

      showToastMessage('Storage cost and notification details are saved successfully.', 'success');

      // Update initial data to reflect saved state and reset changes flag
      setInitialFormData(safeFinalFormData);
      setSelectedTemplates(
        safeFinalTemplateIds.map((id) => ({ notificationTemplateId: id })) as SelectedTemplateProps[],
      );
      // Also update individual state variables to match saved state
      if (safeFinalTemplateIds.length > 0) {
        setSelectedreadyForPickupSms(safeFinalTemplateIds[0] || null);
        setSelectedreadyForPickupEmail(safeFinalTemplateIds[1] || null);
        if (applicable && safeFinalTemplateIds.length >= 4) {
          setSelectedlateFeeSms(safeFinalTemplateIds[2] || null);
          setSelectedlateFeeEmail(safeFinalTemplateIds[3] || null);
        } else {
          setSelectedlateFeeSms(null);
          setSelectedlateFeeEmail(null);
        }
      }

      // Explicitly clear the unsaved changes flag
      setHasChanges(false);
      setHasCreatedRecord(true);

      if (nextPage) {
        navigateToRoleManagement();
      } else {
        fetchStorageCost();
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  const navigateToRoleManagement = () => {
    if (fromSettings) {
      navigate(FrontendRoutes.ROLE_MANAGEMENT, { state: { fromSettings } });
    } else {
      navigate(FrontendRoutes.ROLE_MANAGEMENT);
    }
  };

  // handleNext: validate, then navigate – the blocker will handle unsaved changes
  const handleNext = () => {
    if (!hasCreatedRecord) {
      showToastMessage('Please save the form before proceeding.', 'error');
      return;
    }

    const { isValid, error } = validateFormData();
    if (!isValid) {
      showToastMessage(error || 'Please fill all the required fields.', 'error');
      return;
    }
    // Navigation will be intercepted by the blocker if there are unsaved changes
    navigateToRoleManagement();
  };

  const renderContent = (
    <>
      <Breadcrumbs
        title="Configure Settings"
        activeMainNav="Products"
        activeStep={5}
        subNavSteps={productSubsteps}
        fromSettings={fromSettings ? true : false}
      />
      <div className="mb-4">
        <div className="flex-1 overflow-y-auto">
          <h1 className="font-semibold text-xl mb-3 mt-3">Order Completion</h1>
          <div className="p-2 bg-[#FFFEFA] border border-[#B2B2B2]">
            <div className="flex items-center w-full gap-3">
              <span className="w-[80%] font-semibold text-[#1C1B1F] whitespace-nowrap">
                Estimated Days to Complete an Order
              </span>
              <div className="w-[20%] flex items-center flex-grow">
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      orderEstDays: Math.max(
                        getNumericValue(prev.orderEstDays) - 1,
                        0,
                      ),
                    }))
                  }
                  type="button"
                  className="w-16 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                >
                  -
                </button>
                <input
                  type="number"
                  name="orderEstDays"
                  min="0"
                  value={formData.orderEstDays}
                  className="w-full h-8 text-center border-t border-b"
                  onChange={handleInputChange}
                />
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      orderEstDays: getNumericValue(prev.orderEstDays) + 1,
                    }))
                  }
                  type="button"
                  className="w-16 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <h1 className="font-semibold text-xl mb-3 mt-3">Pickup</h1>
          <div className="p-2 bg-[#FFFEFA] border border-[#B2B2B2]">
            <div className="flex items-center w-full gap-3">
              <span className="w-[80%] font-semibold text-[#1C1B1F] whitespace-nowrap">
                Number of Free Storage Days per Order
              </span>
              <div className="w-[20%] flex items-center flex-grow">
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      freeStorageDays: Math.max(
                        getNumericValue(prev.freeStorageDays) - 1,
                        0,
                      ),
                    }))
                  }
                  type="button"
                  className="w-16 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                >
                  -
                </button>
                <input
                  type="number"
                  name="freeStorageDays"
                  min="0"
                  value={formData.freeStorageDays}
                  className="w-full h-8 text-center border-t border-b"
                  onChange={handleInputChange}
                />
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      freeStorageDays:
                        getNumericValue(prev.freeStorageDays) + 1,
                    }))
                  }
                  type="button"
                  className="w-16 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div className="mt-3 p-2 bg-[#FFFEFA] border border-[#B2B2B2]">
            <div className="flex items-center w-full gap-3">
              <span className="w-[80%] font-semibold text-[#1C1B1F] whitespace-nowrap">
                Number of Times to Resend &quot;Order is Ready for Pickup&quot;
                Notification
              </span>
              <div className="w-[20%] flex items-center flex-grow">
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      resendNotificationCount: Math.max(
                        getNumericValue(prev.resendNotificationCount) - 1,
                        0,
                      ),
                    }))
                  }
                  type="button"
                  className="w-16 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                >
                  -
                </button>
                <input
                  type="number"
                  name="resendNotificationCount"
                  min="0"
                  className="w-full h-8 text-center border-t border-b"
                  onChange={handleInputChange}
                  value={formData.resendNotificationCount}
                />
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      resendNotificationCount:
                        getNumericValue(prev.resendNotificationCount) + 1,
                    }))
                  }
                  type="button"
                  className="w-16 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          {/* Late for Pick Up Storage Charges */}
          <div className="border mt-3 p-4 rounded-md bg-[#FFFEFA] border-[#B2B2B2]">
            <div
              className={`flex justify-between items-center  ${applicable ? 'bg-[#FFFEFA]' : 'bg-gray-200'} p-3 rounded-sm font-semibold`}
            >
              <h2 className="font-semibold">
                Late for Pick Up Storage Charges
              </h2>
              <label className="flex items-center space-x-2">
                <span>{applicable ? 'Applicable' : 'Not Applicable'}</span>
                <div
                  className={`relative rounded-full w-10 h-5 transition duration-200 ease-linear ${applicable ? 'bg-primary' : 'bg-gray-400'
                    }`}
                >
                  <label
                    htmlFor="storageToggle"
                    className={`absolute left-0 bg-white border-2 mb-2 w-5 h-5 rounded-full transition transform duration-100 ease-linear cursor-pointer
                                         ${applicable
                        ? 'translate-x-full border-primary'
                        : 'translate-x-0 border-gray-400'
                      }`}
                  ></label>
                  <input
                    type="checkbox"
                    id="storageToggle"
                    name="storageToggle"
                    checked={applicable}
                    onChange={() => {
                      const newValue = !applicable;
                      setApplicable(newValue);
                      setFormData((prev) => ({
                        ...prev,
                        isApplicable: newValue ? 1 : 0,
                      }));
                      if (!newValue) {
                        setSelectedlateFeeSms(null);
                        setSelectedlateFeeEmail(null);
                        setFormData((prev) => ({
                          ...prev,
                          notificationDays: 0,
                          storageCostPerDay: 0,
                        }));
                      }
                    }}
                    className="appearance-none w-full h-full active:outline-none focus:outline-none"
                  />
                </div>
              </label>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <span>
                No. of days to notify customer before storage charges apply
              </span>
              <div className="flex items-center space-x-2 ">
                <button
                  disabled={!applicable}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      notificationDays: Math.max(
                        getNumericValue(prev.notificationDays) - 1,
                        0,
                      ),
                    }))
                  }
                  type="button"
                  className={`px-3 py-1 border rounded text-white h-10 ${!applicable ? 'bg-[#D9D9D9] cursor-not-allowed' : 'bg-primary hover:bg-dark-primary'}`}
                >
                  -
                </button>
                <input
                  type="number"
                  name="notificationDays"
                  min="0"
                  onChange={handleInputChange}
                  className="rounded-none h-10 border p-2 ml-0 w-14 text-center"
                  style={{ marginLeft: 0 }}
                  disabled={!applicable}
                  value={formData.notificationDays}
                />
                <button
                  type="button"
                  className={`h-10 px-3 py-1 border rounded text-white ${!applicable ? 'bg-[#D9D9D9] cursor-not-allowed' : 'bg-primary hover:bg-dark-primary'}`}
                  style={{ marginLeft: 0 }}
                  disabled={!applicable}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      notificationDays:
                        getNumericValue(prev.notificationDays) + 1,
                    }))
                  }
                >
                  +
                </button>
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <div className="flex">
                <span>Storage charges after free storage days</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <div className="flex items-center border rounded-md text-gray-600 focus-within:outline-primary">
                    <span className="px-2 text-gray-600">$</span>
                    <input
                      name="storageCostPerDay"
                      type="number"
                      step="0.01"
                      min="0"
                      disabled={!applicable}
                      value={formData.storageCostPerDay}
                      onChange={(e) => {
                        const val: number | string =
                          e.target.value === ''
                            ? ''
                            : parseFloat(e.target.value) || 0;
                        setFormData((prev) => ({
                          ...prev,
                          storageCostPerDay: val,
                        }));
                      }}
                      className="w-16 p-2 focus:outline-none text-gray-600"
                    />
                  </div>
                </div>
                <span>/ Day</span>
              </div>
            </div>
          </div>
          {/* Order is Ready for Pick Up - Notification */}
          <div className="border rounded-md shadow-sm mb-3 mt-4 bg-[#FFFEFA] border-[#B2B2B2]">
            <button
              type="button"
              onClick={() => toggleAccordion('pickupReady')}
              className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
            >
              <span>Order is Ready for Pick Up - Notification</span>
              <div className="flex items-center gap-4">
                <FontAwesomeIcon
                  className="text-[#000A16]"
                  icon={
                    openAccordions['pickupReady'] ? faChevronUp : faChevronDown
                  }
                />
              </div>
            </button>
            {openAccordions['pickupReady'] && (
              <div className="p-4 rounded-md">
                <p className="text-sm text-gray-600">
                  This Notification is sent to the customer at generate invoice.
                </p>

                {Object.entries(
                  notificationTemplates[PickUpSettingsList.OrderIsReadyTemplates] || {},
                ).map(([innerKey, templates]) => (
                  <div key={innerKey} className="mt-4">
                    <span className="font-semibold text-[#1C1B1F]">
                      {innerKey === '1'
                        ? 'Choose SMS Template for order is ready for pickup'
                        : 'Choose Email Template for order is ready for pickup'}
                    </span>

                    <div className="mt-3">
                      {templates.map((template, index) => (
                        <div
                          key={template.id}
                          className={`${innerKey === '1'
                            ? 'flex items-start gap-2'
                            : 'flex justify-between'
                            } rounded-md p-2`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="relative flex items-center justify-center w-4 h-4">
                              <input
                                type="radio"
                                name={`group_1_${innerKey}`}
                                value={template.id}
                                checked={
                                  innerKey === '1'
                                    ? isTemplateChecked(selectedreadyForPickupSms, template.id)
                                    : isTemplateChecked(selectedreadyForPickupEmail, template.id)
                                }
                                onChange={() =>
                                  innerKey === '1'
                                    ? setSelectedreadyForPickupSms(template.id)
                                    : setSelectedreadyForPickupEmail(template.id)
                                }
                                className={`w-4 h-4 bg-white border-2 rounded-full focus:ring-2 focus:ring-black focus:ring-offset-0 appearance-none ${(innerKey === '1'
                                  ? isTemplateChecked(selectedreadyForPickupSms, template.id)
                                  : isTemplateChecked(selectedreadyForPickupEmail, template.id))
                                  ? 'border-black'
                                  : 'border-gray-400'
                                  }`}
                              />
                              {(innerKey === '1'
                                ? isTemplateChecked(selectedreadyForPickupSms, template.id)
                                : isTemplateChecked(selectedreadyForPickupEmail, template.id)) && (
                                  <div className="absolute w-2 h-2 bg-[#374151] rounded-full pointer-events-none top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                                )}
                            </div>

                            <p className="text-sm text-gray-700 leading-none">
                              {innerKey === '1'
                                ? template.templateContent
                                : `Template ${index + 1}`}
                            </p>
                          </div>

                          {innerKey === '2' && (
                            <div className="flex items-start gap-2">
                              <button
                                className="text-primary font-semibold"
                                type="button"
                                onClick={() =>
                                  handleViewEmailTemplate(
                                    template.templateContent,
                                  )
                                }
                              >
                                <span>
                                  View Email Template
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className="ml-2"
                                  />
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Late fee applied */}
          {!!applicable && (
            <div className="border rounded-md shadow-sm mb-3 mt-4 bg-[#FFFEFA] border-[#B2B2B2]">
              <button
                type="button"
                onClick={() => toggleAccordion('lateFee')}
                className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg focus:outline-primary"
              >
                <span>Late fee applied</span>
                <div className="flex items-center gap-4">
                  <FontAwesomeIcon
                    className="text-[#000A16]"
                    icon={
                      openAccordions['lateFee'] ? faChevronUp : faChevronDown
                    }
                  />
                </div>
              </button>
              {openAccordions['lateFee'] && (
                <div className="p-4 rounded-md">
                  <p className="text-sm text-[#1C1B1F]">
                    This Notification is sent to the customer at the day the
                    storage charges begin
                  </p>

                  {Object.entries(
                    notificationTemplates[PickUpSettingsList.LateFeeAppliedTemplates] || {},
                  ).map(([innerKey, templates]) => (
                    <div key={innerKey} className="mt-4">
                      <span className="font-semibold text-[#1C1B1F]">
                        {innerKey === '1'
                          ? 'Choose SMS Template for late fee applied'
                          : 'Choose Email Template for late fee applied'}
                      </span>

                      <div className="mt-3 rounded-md">
                        {templates.map((template, index) => (
                          <div
                            key={template.id}
                            className={`${innerKey === '1'
                              ? 'flex items-start gap-2'
                              : 'flex justify-between'
                              } rounded-md p-2`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="relative mt-1">
                                <input
                                  type="radio"
                                  name={`group_2_${innerKey}`}
                                  value={template.id}
                                  checked={
                                    innerKey === '1'
                                      ? isTemplateChecked(
                                        selectedlateFeeSms,
                                        template.id,
                                      )
                                      : isTemplateChecked(
                                        selectedlateFeeEmail,
                                        template.id,
                                      )
                                  }
                                  onChange={() =>
                                    innerKey === '1'
                                      ? setSelectedlateFeeSms(template.id)
                                      : setSelectedlateFeeEmail(template.id)
                                  }
                                  className={`w-4 h-4 bg-white border-2 rounded-full focus:ring-2 focus:ring-black focus:ring-offset-0 appearance-none ${(
                                    innerKey === '1'
                                      ? isTemplateChecked(
                                        selectedlateFeeSms,
                                        template.id,
                                      )
                                      : isTemplateChecked(
                                        selectedlateFeeEmail,
                                        template.id,
                                      )
                                  )
                                    ? 'border-black'
                                    : 'border-gray-400'
                                    }`}
                                />
                                {(innerKey === '1'
                                  ? isTemplateChecked(
                                    selectedlateFeeSms,
                                    template.id,
                                  )
                                  : isTemplateChecked(
                                    selectedlateFeeEmail,
                                    template.id,
                                  )) && (
                                    <div className="absolute w-2 h-2 bg-[#374151] rounded-full pointer-events-none top-[10px] left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                                  )}
                              </div>
                              <p className="text-sm text-gray-700">
                                {innerKey === '1'
                                  ? template.templateContent
                                  : `Template ${index + 1}`}
                              </p>
                            </div>

                            {innerKey === '2' && (
                              <div className="flex items-start gap-2">
                                <button
                                  className="text-primary font-semibold"
                                  type="button"
                                  onClick={() =>
                                    handleViewEmailTemplate(
                                      template.templateContent,
                                    )
                                  }
                                >
                                  <span>
                                    View Email Template
                                    <FontAwesomeIcon
                                      icon={faEye}
                                      className="ml-2"
                                    />
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {showModal && selectedEmailTemplate && (
            <EmailTemplateModal
              onClose={() => setShowModal(false)}
              htmlContent={selectedEmailTemplate}
            />
          )}
        </div>
      </div>
      {/* Actions Container Wrapper */}
      <div className="sticky bottom-0 bg-white w-full mt-6 pt-4 pb-4 border-t border-gray-100 z-10">
        <div className="w-full flex items-center justify-between 2xl:justify-end gap-4">
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            disabled={!hasChanges}
            className={`w-1/2 2xl:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasChanges
              ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
              : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
              }`}
          >
            Save
          </button>

          <button
            onClick={handleNext}
            type="button"
            disabled={!hasCreatedRecord || hasChanges}
            className={`w-1/2 2xl:w-[150px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200 ${!hasCreatedRecord || hasChanges
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                : 'border-transparent text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
              }`}
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};